import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Card, CardContent } from '@repo/shared-client/components/ui'
import { useTranslation } from '@web/src/lib/intl/react'
import { CheckCircleIcon, CircleDollarSignIcon, CircleSlashIcon, UsersIcon, UserXIcon } from 'lucide-react'
import SFSpinner from 'src/components/primatives/sf-spinner/sf-spinner'
import type { EventSummaryQuery, EventSummaryQueryVariables } from 'types/graphql'

export const EVENT_SUMMARY_QUERY = gql`
  query EventSummaryQuery($eventId: String!) {
    eventSummary(eventId: $eventId) {
      totalGoingAttendees
      totalWaitlistAttendees
      totalFeesCollected
      totalNoFeeAttendees
      totalPaidAttendees
      totalPartialAttendees
      totalUnpaidAttendees
    }
  }
`

interface EventSummaryProps {
  eventId: string
  event: {
    waitlistEnabled?: boolean
  }
}

function StatCard(props: { icon: React.ReactNode; value: React.ReactNode; label: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2">
        <div className="flex items-center justify-center">{props.icon}</div>
        <div className="font-bold text-2xl">{props.value}</div>
        <div className="text-center text-muted-foreground text-sm">{props.label}</div>
      </CardContent>
    </Card>
  )
}

const EventSummary = ({ eventId, event }: EventSummaryProps) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<EventSummaryQuery, EventSummaryQueryVariables>(EVENT_SUMMARY_QUERY, {
    variables: { eventId },
  })

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <SFSpinner size="md" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">
            {t('Error loading event summary: {{message}}', { message: error.message })}
          </p>
        </CardContent>
      </Card>
    )
  }

  const summary = data?.eventSummary

  if (!summary) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p>{t('No summary data available')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        <StatCard
          icon={<UsersIcon className="size-8 text-muted-foreground" />}
          label={t('Total Going Attendees')}
          value={summary.totalGoingAttendees}
        />

        {event?.waitlistEnabled && (
          <StatCard
            icon={<UsersIcon className="size-8 text-amber-400" />}
            label={t('Total Waitlisted Attendees')}
            value={summary.totalWaitlistAttendees}
          />
        )}

        <StatCard
          icon={<CircleDollarSignIcon className="size-8 text-green-600" />}
          label={t('Total Fees Collected')}
          value={`$${summary.totalFeesCollected.toFixed(2)}`}
        />

        <StatCard
          icon={<CheckCircleIcon className="size-8 text-purple-500" />}
          label={t('No Fee Attendees')}
          value={summary.totalNoFeeAttendees}
        />

        <StatCard
          icon={<CheckCircleIcon className="size-8 text-green-600" />}
          label={t('Paid Attendees')}
          value={summary.totalPaidAttendees}
        />

        <StatCard
          icon={<CircleSlashIcon className="size-8 text-amber-500" />}
          label={t('Partial Paid Attendees')}
          value={summary.totalPartialAttendees}
        />

        <StatCard
          icon={<UserXIcon className="size-8 text-red-500" />}
          label={t('Unpaid Attendees')}
          value={summary.totalUnpaidAttendees}
        />
      </div>
    </div>
  )
}

export default EventSummary
